import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet,
  TextInput, Modal, ActivityIndicator, ScrollView, Switch
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../../hooks/useAuth';
import {
  getCategories, createCategory, updateCategory, deleteCategory,
  getEquipmentByCategory, createEquipment, updateEquipment, deleteEquipment
} from '../../services/inventoryService';
import { Category, Equipment, EquipmentItemType } from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { useLanguage } from '../../contexts/LanguageContext';
import { NavBackButton } from '../../components/NavBackButton';
import { categoryTracking } from '../../utils/categoryTracking';

// Returns all descendant IDs of a given category
function getDescendantIds(catId: string, allCats: Category[]): string[] {
  const children = allCats.filter(c => c.parentId === catId);
  return children.flatMap(c => [c.id, ...getDescendantIds(c.id, allCats)]);
}

export default function CategoriesScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const navigation = useNavigation();

  // Breadcrumb navigation stack
  const [categoryPath, setCategoryPath] = useState<Category[]>([]);
  // All categories (for pickers)
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  // Children of the current level
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  // Equipment items shown when in leaf mode
  const [equipmentItems, setEquipmentItems] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);

  // Category modal state
  const [catModalVisible, setCatModalVisible] = useState(false);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', parentId: null as string | null, weight: 0, requiresActionNumber: false, tracksExpiry: false, tracksReview: false });
  const [catPickerExpanded, setCatPickerExpanded] = useState(false);

  // Equipment modal state
  const [eqModalVisible, setEqModalVisible] = useState(false);
  const [editingEq, setEditingEq] = useState<Equipment | null>(null);
  const [eqForm, setEqForm] = useState({ name: '', unit: '', description: '', categoryId: '', minQuantity: '0', tracksExpiry: false, tracksReview: false, itemType: 'disposable' as EquipmentItemType });
  const [eqPickerExpanded, setEqPickerExpanded] = useState(false);

  const canManage = appUser?.role === 'admin' || appUser?.role === 'manager';
  const canView = appUser?.role !== 'guest';

  const currentCategory = categoryPath.length > 0 ? categoryPath[categoryPath.length - 1] : null;
  const categoryMap = new Map(allCategories.map(category => [category.id, category]));
  const catInherited = categoryTracking(catForm.parentId, categoryMap);
  const eqInherited = categoryTracking(eqForm.categoryId, categoryMap);

  // A category is a leaf when it has no subcategories and we are inside it (not at root)
  const isLeafMode = currentCategory !== null && subcategories.length === 0;
  // A category is empty (no subcategories and no inventory) — shows both add options
  const isEmpty = isLeafMode && equipmentItems.length === 0;

  useLayoutEffect(() => {
    if (categoryPath.length > 0) {
      navigation.setOptions({
        title: currentCategory?.name ?? t('tabs.inventory'),
        headerLeft: () => <NavBackButton onPress={() => setCategoryPath(prev => prev.slice(0, -1))} />,
      });
    } else {
      navigation.setOptions({
        title: t('tabs.inventory'),
        headerLeft: () => <NavBackButton />,
      });
    }
  }, [categoryPath, navigation, currentCategory, t]);

  const loadData = useCallback(async (currentCat: Category | null) => {
    setLoading(true);
    try {
      const allCats = await getCategories();
      setAllCategories(allCats);
      const parentId = currentCat?.id ?? null;
      const children = allCats.filter(c => (c.parentId ?? null) === parentId);
      setSubcategories(children);

      if (currentCat !== null && children.length === 0) {
        const items = await getEquipmentByCategory(currentCat.id);
        setEquipmentItems(items);
      } else {
        setEquipmentItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(currentCategory);
  }, [currentCategory, loadData]);

  // ── Category CRUD ──────────────────────────────────────────────────────────

  const openCreateCategory = () => {
    setEditingCat(null);
    setCatForm({ name: '', description: '', parentId: currentCategory?.id ?? null, weight: 0, requiresActionNumber: false, tracksExpiry: false, tracksReview: false });
    setCatModalVisible(true);
  };

  const openEditCategory = (cat: Category) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, description: cat.description ?? '', parentId: cat.parentId ?? null, weight: cat.weight ?? 0, requiresActionNumber: cat.requiresActionNumber ?? false, tracksExpiry: !!cat.tracksExpiry, tracksReview: !!cat.tracksReview });
    setCatPickerExpanded(false);
    setCatModalVisible(true);
  };

  const handleSaveCategory = async () => {
    if (!catForm.name.trim()) {
      CrossPlatformAlert.alert(t('common.validation'), t('categories.validationMessage'));
      return;
    }
    try {
      if (editingCat) {
        await updateCategory(editingCat.id, catForm.name, catForm.description, catForm.parentId, catForm.weight, catForm.requiresActionNumber, { tracksExpiry: catForm.tracksExpiry, tracksReview: catForm.tracksReview });
      } else {
        await createCategory(catForm.name, catForm.description, catForm.parentId, catForm.weight, catForm.requiresActionNumber, { tracksExpiry: catForm.tracksExpiry, tracksReview: catForm.tracksReview });
      }
      setCatModalVisible(false);
      loadData(currentCategory);
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    }
  };

  const handleDeleteCategory = (cat: Category) => {
    CrossPlatformAlert.alert(t('common.delete'), t('categories.confirmDelete', cat.name), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'), style: 'destructive', onPress: async () => {
          try {
            await deleteCategory(cat.id);
            loadData(currentCategory);
          } catch (err: any) {
            CrossPlatformAlert.alert(t('common.error'), err.message);
          }
        }
      }
    ]);
  };

  // ── Equipment CRUD ─────────────────────────────────────────────────────────

  const openCreateEquipment = () => {
    if (!currentCategory) return;
    setEditingEq(null);
    setEqForm({ name: '', unit: '', description: '', categoryId: currentCategory.id, minQuantity: '0', tracksExpiry: false, tracksReview: false, itemType: 'disposable' });
    setEqModalVisible(true);
  };

  const openEditEquipment = (item: Equipment) => {
    setEditingEq(item);
    setEqForm({ name: item.name, unit: item.unit, description: item.description ?? '', categoryId: item.categoryId, minQuantity: String(item.minQuantity ?? 0), tracksExpiry: item.ownTracking?.tracksExpiry ?? !!item.tracksExpiry, tracksReview: item.ownTracking?.tracksReview ?? !!item.tracksReview, itemType: item.itemType ?? 'disposable' });
    setEqPickerExpanded(false);
    setEqModalVisible(true);
  };

  const handleSaveEquipment = async () => {
    if (!eqForm.name.trim() || !eqForm.unit.trim()) {
      CrossPlatformAlert.alert(t('common.validation'), t('inventory.validationMessage'));
      return;
    }
    const minQty = parseInt(eqForm.minQuantity, 10);
    const parsedMinQty = isNaN(minQty) || minQty < 0 ? 0 : minQty;
    try {
      if (editingEq) {
        await updateEquipment(editingEq.id, eqForm.name, eqForm.categoryId, eqForm.unit, eqForm.description, parsedMinQty, {tracksExpiry: eqForm.tracksExpiry, tracksReview: eqForm.tracksReview}, eqForm.itemType);
      } else {
        if (!currentCategory) return;
        await createEquipment(eqForm.name, currentCategory.id, eqForm.unit, eqForm.description, parsedMinQty, { tracksExpiry: eqForm.tracksExpiry, tracksReview: eqForm.tracksReview }, eqForm.itemType);
      }
      setEqModalVisible(false);
      loadData(currentCategory);
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    }
  };

  const handleDeleteEquipment = (item: Equipment) => {
    CrossPlatformAlert.alert(t('common.delete'), t('inventory.confirmDelete', item.name), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'), style: 'destructive', onPress: async () => {
          await deleteEquipment(item.id);
          loadData(currentCategory);
        }
      }
    ]);
  };

  // ── Derived data for pickers ───────────────────────────────────────────────

  // Valid parent categories for the category being edited (excludes self and descendants)
  const parentCandidates = editingCat
    ? allCategories.filter(c => {
        const excluded = new Set([editingCat.id, ...getDescendantIds(editingCat.id, allCategories)]);
        return !excluded.has(c.id);
      })
    : [];

  // Leaf categories (no children) available for equipment reassignment
  const leafCategories = allCategories.filter(
    c => !allCategories.some(other => other.parentId === c.id)
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!canView) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('common.noAccess')}</Text></View>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>

      {/* Breadcrumb */}
      {categoryPath.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.breadcrumbBar}
          contentContainerStyle={styles.breadcrumbContent}
        >
          <TouchableOpacity onPress={() => setCategoryPath([])}>
            <Text style={styles.breadcrumbItem}>{t('categories.title')}</Text>
          </TouchableOpacity>
          {categoryPath.map((cat, idx) => (
            <React.Fragment key={cat.id}>
              <Text style={styles.breadcrumbSep}>{'›'}</Text>
              <TouchableOpacity onPress={() => setCategoryPath(categoryPath.slice(0, idx + 1))}>
                <Text style={[
                  styles.breadcrumbItem,
                  idx === categoryPath.length - 1 && styles.breadcrumbActive
                ]}>
                  {cat.name}
                </Text>
              </TouchableOpacity>
            </React.Fragment>
          ))}
        </ScrollView>
      )}

      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title} numberOfLines={1}>
          {currentCategory?.name ?? t('categories.title')}
        </Text>
        {canManage && (
          <View style={styles.headerActions}>
            {/* In category/root mode: show Add Category/Subcategory button */}
            {!isLeafMode && (
              <TouchableOpacity style={styles.addBtn} onPress={openCreateCategory}>
                <Text style={styles.addBtnText}>{t('common.add')}</Text>
              </TouchableOpacity>
            )}
            {/* In leaf mode: show Add Equipment button */}
            {isLeafMode && (
              <TouchableOpacity style={styles.addBtn} onPress={openCreateEquipment}>
                <Text style={styles.addBtnText}>{t('common.add')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {loading ? (
        <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
      ) : isLeafMode ? (
        /* ── Leaf mode: show equipment ── */
        <FlatList
          data={equipmentItems}
          keyExtractor={item => item.id}
          renderItem={({ item }) => (
            <View style={styles.card}>
              <View style={styles.cardBody}>
                <Text style={styles.itemName}>{item.name}</Text>
                <Text style={styles.itemMeta}>{t('inventory.unitLabel')}: {item.unit}</Text>
                <Text style={styles.itemMeta}>{t(`inventory.${item.itemType ?? 'disposable'}`)}</Text>
                {item.minQuantity > 0 && (
                  <Text style={styles.itemMeta}>{t('inventory.minQuantityLabel')}: {item.minQuantity}</Text>
                )}
                {item.description ? <Text style={styles.itemDesc}>{item.description}</Text> : null}
              </View>
              {canManage && (
                <View style={styles.actions}>
                  <TouchableOpacity onPress={() => openEditEquipment(item)} style={styles.editBtn}>
                    <Text style={styles.editText}>{t('common.edit')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handleDeleteEquipment(item)} style={styles.deleteBtn}>
                    <Text style={styles.deleteText}>{t('common.delete')}</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.empty}>{t('categories.noInventoryItems')}</Text>
              {canManage && isEmpty && (
                <TouchableOpacity style={styles.altAddBtn} onPress={openCreateCategory}>
                  <Text style={styles.altAddText}>{t('categories.addSubcategoryHere')}</Text>
                </TouchableOpacity>
              )}
            </View>
          }
        />
      ) : (
        /* ── Category/root mode: show subcategories ── */
        <FlatList
          data={subcategories}
          keyExtractor={item => item.id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.card}
              onPress={() => setCategoryPath(prev => [...prev, item])}
              activeOpacity={0.7}
            >
              <View style={styles.cardBody}>
                <Text style={styles.itemName}>{item.name}</Text>
                {item.description ? <Text style={styles.itemDesc}>{item.description}</Text> : null}
              </View>
              <View style={styles.actions}>
                <TouchableOpacity
                  onPress={(e) => { e.stopPropagation(); setCategoryPath(prev => [...prev, item]); }}
                  style={styles.openBtn}
                >
                  <Text style={styles.openBtnText}>{t('common.open')}</Text>
                </TouchableOpacity>
                {canManage && (
                  <>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); openEditCategory(item); }}
                      style={styles.editBtn}
                    >
                      <Text style={styles.editText}>{t('common.edit')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); handleDeleteCategory(item); }}
                      style={styles.deleteBtn}
                    >
                      <Text style={styles.deleteText}>{t('common.delete')}</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </TouchableOpacity>
          )}
          ListEmptyComponent={<Text style={styles.empty}>{t('categories.noItems')}</Text>}
        />
      )}

      </View>

      {/* Category Modal */}
      <Modal visible={catModalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.modalTitle}>
                {editingCat
                  ? t('categories.editTitle')
                  : currentCategory
                    ? t('categories.addSubcategoryTitle')
                    : t('categories.addTitle')}
              </Text>
              <TextInput
                style={styles.input}
                placeholder={t('common.namePlaceholder')}
                value={catForm.name}
                onChangeText={v => setCatForm(f => ({ ...f, name: v }))}
              />
              <TextInput
                style={[styles.input, { height: 80 }]}
                placeholder={t('common.descriptionPlaceholder')}
                value={catForm.description}
                onChangeText={v => setCatForm(f => ({ ...f, description: v }))}
                multiline
              />
              <Text style={styles.trackingHint}>{t('operations.categoryTrackingHint')}</Text>
              {(['tracksExpiry', 'tracksReview'] as const).map(field => <View key={field} style={styles.trackingRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.checkboxLabel}>{t(field === 'tracksExpiry' ? 'operations.expirySetting' : 'operations.reviewSetting')}</Text>
                  {catInherited[field] && <Text style={styles.trackingHint}>{t('operations.inheritedTracking')}</Text>}
                </View>
                <Switch accessibilityLabel={t(field === 'tracksExpiry' ? 'operations.expirySetting' : 'operations.reviewSetting')} value={catForm[field] || catInherited[field]} disabled={catInherited[field]} onValueChange={checked => setCatForm(prev => ({ ...prev, [field]: checked }))} />
              </View>)}
              <Text style={styles.label}>{t('categories.weightLabel')}</Text>
              <TextInput
                style={styles.input}
                placeholder={t('categories.weightPlaceholder')}
                value={String(catForm.weight)}
                onChangeText={v => setCatForm(f => ({ ...f, weight: parseInt(v, 10) || 0 }))}
                keyboardType="numeric"
              />
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => setCatForm(f => ({ ...f, requiresActionNumber: !f.requiresActionNumber }))}
                activeOpacity={0.7}
              >
                <View style={[styles.checkbox, catForm.requiresActionNumber && styles.checkboxChecked]}>
                  {catForm.requiresActionNumber && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>{t('categories.requiresActionNumber')}</Text>
              </TouchableOpacity>
              {editingCat && (
                <>
                  <Text style={styles.label}>{t('categories.parentCategoryLabel')}</Text>
                  {!catPickerExpanded ? (
                    <View style={styles.selectedValueRow}>
                      <Text style={styles.selectedValueText}>
                        {catForm.parentId === null
                          ? t('categories.rootCategory')
                          : (parentCandidates.find(c => c.id === catForm.parentId)?.name ?? t('categories.rootCategory'))}
                      </Text>
                      <TouchableOpacity style={styles.changeBtn} onPress={() => setCatPickerExpanded(true)}>
                        <Text style={styles.changeBtnText}>{t('common.change')}</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <View style={styles.pickerWrapper}>
                      <TouchableOpacity
                        style={[styles.pickerOption, catForm.parentId === null && styles.pickerSelected]}
                        onPress={() => { setCatForm(f => ({ ...f, parentId: null })); setCatPickerExpanded(false); }}
                      >
                        <Text style={[styles.pickerOptionText, catForm.parentId === null && styles.pickerSelectedText]}>
                          {t('categories.rootCategory')}
                        </Text>
                      </TouchableOpacity>
                      {parentCandidates.map(c => (
                        <TouchableOpacity
                          key={c.id}
                          style={[styles.pickerOption, catForm.parentId === c.id && styles.pickerSelected]}
                          onPress={() => { setCatForm(f => ({ ...f, parentId: c.id })); setCatPickerExpanded(false); }}
                        >
                          <Text style={[styles.pickerOptionText, catForm.parentId === c.id && styles.pickerSelectedText]}>
                            {c.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </>
              )}
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setCatModalVisible(false)}>
                  <Text style={styles.cancelText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveCategory}>
                  <Text style={styles.saveText}>{t('common.save')}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Equipment Modal */}
      <Modal visible={eqModalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView keyboardShouldPersistTaps="handled">
              <Text style={styles.modalTitle}>
                {editingEq ? t('inventory.editTitle') : t('inventory.addTitle')}
              </Text>
              <TextInput
                style={styles.input}
                placeholder={t('common.namePlaceholder')}
                value={eqForm.name}
                onChangeText={v => setEqForm(f => ({ ...f, name: v }))}
              />
              <TextInput
                style={styles.input}
                placeholder={t('inventory.unitPlaceholder')}
                value={eqForm.unit}
                onChangeText={v => setEqForm(f => ({ ...f, unit: v }))}
              />
              <TextInput
                style={[styles.input, { height: 80 }]}
                placeholder={t('common.descriptionPlaceholder')}
                value={eqForm.description}
                onChangeText={v => setEqForm(f => ({ ...f, description: v }))}
                multiline
              />
              <Text style={styles.label}>{t('inventory.itemType')}</Text>
              <View style={styles.itemTypes}>
                {(['disposable', 'reusable'] as const).map(itemType => <TouchableOpacity key={itemType}
                  accessibilityRole="radio" accessibilityState={{ checked: eqForm.itemType === itemType }}
                  aria-checked={eqForm.itemType === itemType}
                  onPress={() => setEqForm(form => ({ ...form, itemType }))}
                  style={[styles.itemTypeOption, eqForm.itemType === itemType && styles.itemTypeSelected]}>
                  <Text style={eqForm.itemType === itemType ? styles.pickerSelectedText : styles.pickerOptionText}>{t(`inventory.${itemType}`)}</Text>
                </TouchableOpacity>)}
              </View>
              {eqForm.itemType === 'reusable' && <Text style={styles.trackingHint}>{t('inventory.reusableHint')}</Text>}
              <Text style={styles.label}>{t('inventory.minQuantityLabel')}</Text>
              <View style={styles.qtyRow}>
                <TouchableOpacity
                  style={styles.qtyBtn}
                  onPress={() => setEqForm(f => ({ ...f, minQuantity: String(Math.max(0, (parseInt(f.minQuantity, 10) || 0) - 1)) }))}
                >
                  <Text style={styles.qtyBtnText}>−</Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.qtyInput}
                  value={eqForm.minQuantity}
                  onChangeText={v => setEqForm(f => ({ ...f, minQuantity: v }))}
                  keyboardType="number-pad"
                />
                <TouchableOpacity
                  style={styles.qtyBtn}
                  onPress={() => setEqForm(f => ({ ...f, minQuantity: String((parseInt(f.minQuantity, 10) || 0) + 1) }))}
                >
                  <Text style={styles.qtyBtnText}>+</Text>
                </TouchableOpacity>
              </View>
              {(['tracksExpiry', 'tracksReview'] as const).map(field => <View key={field} style={styles.trackingRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.checkboxLabel}>{t(field === 'tracksExpiry' ? 'operations.expirySetting' : 'operations.reviewSetting')}</Text>
                  {eqInherited[field] && <Text style={styles.trackingHint}>{t('operations.inheritedTracking')}</Text>}
                </View>
                <Switch accessibilityLabel={t(field === 'tracksExpiry' ? 'operations.expirySetting' : 'operations.reviewSetting')} value={eqForm[field] || eqInherited[field]} disabled={eqInherited[field]} onValueChange={checked => setEqForm(prev => ({...prev, [field]: checked}))} />
              </View>)}
              {editingEq && (
                <>
                  <Text style={styles.label}>{t('inventory.categoryLabel')}</Text>
                  {!eqPickerExpanded ? (
                    <View style={styles.selectedValueRow}>
                      <Text style={styles.selectedValueText}>
                        {leafCategories.find(c => c.id === eqForm.categoryId)?.name ?? '—'}
                      </Text>
                      <TouchableOpacity style={styles.changeBtn} onPress={() => setEqPickerExpanded(true)}>
                        <Text style={styles.changeBtnText}>{t('common.change')}</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <View style={styles.pickerWrapper}>
                      {leafCategories.map(c => (
                        <TouchableOpacity
                          key={c.id}
                          style={[styles.pickerOption, eqForm.categoryId === c.id && styles.pickerSelected]}
                          onPress={() => { setEqForm(f => ({ ...f, categoryId: c.id })); setEqPickerExpanded(false); }}
                        >
                          <Text style={[styles.pickerOptionText, eqForm.categoryId === c.id && styles.pickerSelectedText]}>
                            {c.name}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                </>
              )}
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setEqModalVisible(false)}>
                  <Text style={styles.cancelText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={handleSaveEquipment}>
                  <Text style={styles.saveText}>{t('common.save')}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  itemTypes: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  itemTypeOption: { flex: 1, padding: 10, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, alignItems: 'center' },
  itemTypeSelected: { backgroundColor: '#EFF6FF', borderColor: '#2563EB' },
  trackingRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 },
  trackingHint: { fontSize: 12, color: '#64748B', marginBottom: 8 },
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  breadcrumbBar: { backgroundColor: '#EFF6FF', maxHeight: 40 },
  breadcrumbContent: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8 },
  breadcrumbItem: { fontSize: 13, color: '#2563EB' },
  breadcrumbActive: { color: '#1E293B', fontWeight: '600' },
  breadcrumbSep: { fontSize: 13, color: '#94A3B8', marginHorizontal: 4 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  title: { flex: 1, fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  headerActions: { flexDirection: 'row', gap: 8 },
  addBtn: { backgroundColor: '#2563EB', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  addBtnText: { color: '#FFF', fontWeight: '600' },
  card: { backgroundColor: '#FFF', margin: 8, borderRadius: 12, padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1 },
  cardBody: { flex: 1 },
  itemName: { fontSize: 16, fontWeight: '600', color: '#1E293B' },
  itemMeta: { fontSize: 13, color: '#64748B', marginTop: 4 },
  itemDesc: { fontSize: 13, color: '#94A3B8', marginTop: 4 },
  actions: { flexDirection: 'row', gap: 8, marginLeft: 8, alignItems: 'center' },
  openBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6 },
  openBtnText: { color: '#2563EB', fontSize: 12, fontWeight: '600' },
  editBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 },
  editText: { color: '#2563EB', fontSize: 13, fontWeight: '600' },
  deleteBtn: { backgroundColor: '#FEF2F2', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 },
  deleteText: { color: '#EF4444', fontSize: 13, fontWeight: '600' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 16 },
  emptyContainer: { alignItems: 'center' },
  altAddBtn: { marginTop: 16, backgroundColor: '#F0FDF4', borderWidth: 1, borderColor: '#22C55E', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  altAddText: { color: '#16A34A', fontWeight: '600', fontSize: 14 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { backgroundColor: '#FFF', borderRadius: 20, padding: 24, maxHeight: '85%', width: '100%', maxWidth: 520 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#1E293B', marginBottom: 16 },
  label: { fontSize: 14, color: '#64748B', marginBottom: 4 },
  input: { borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 12, fontSize: 15, marginBottom: 12 },
  pickerWrapper: { borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  pickerOption: { padding: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  pickerSelected: { backgroundColor: '#EFF6FF' },
  pickerOptionText: { fontSize: 15, color: '#1E293B' },
  pickerSelectedText: { color: '#2563EB', fontWeight: '600' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  cancelText: { color: '#64748B', fontWeight: '600' },
  saveBtn: { backgroundColor: '#2563EB', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  saveText: { color: '#FFF', fontWeight: '600' },
  qtyRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  qtyBtn: { backgroundColor: '#E2E8F0', width: 36, height: 36, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  qtyBtnText: { fontSize: 20, color: '#1E293B', lineHeight: 24 },
  qtyInput: { flex: 1, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 8, fontSize: 15, textAlign: 'center', marginHorizontal: 8 },
  selectedValueRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 12, marginBottom: 12 },
  selectedValueText: { fontSize: 15, color: '#1E293B', flex: 1 },
  changeBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, marginLeft: 8 },
  changeBtnText: { color: '#2563EB', fontSize: 13, fontWeight: '600' },
  checkboxRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16, paddingVertical: 4 },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: '#CBD5E1', alignItems: 'center', justifyContent: 'center' },
  checkboxChecked: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  checkmark: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  checkboxLabel: { fontSize: 15, color: '#1E293B', flex: 1 },
});


